import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, ErrorAlert, Field } from "src/components";
import DropdownAnimation from "src/components/UI/dropdownAnimation/DropdownAnimation";
import {
  ALFRED_FIAT_ACCOUNT_TYPE_BY_COUNTRY,
  ALFRED_FIAT_CURRENCY_TO_COUNTRY,
  AlfredFiatAccountFields,
  AlfredFiatAccountType,
  CreateFiatAccountPayload,
} from "src/api";
import { FieldError, RAIL_RULES, RuleDef, validateFiatAccountFields } from "./alfred-fiat-account-rules";
import "./alfred-add-bank-form.scss";

interface Props {
  sourceCurrency: string | null;
  submitting: boolean;
  error: string | null;
  onSubmit: (payload: Omit<CreateFiatAccountPayload, "session_id">) => void;
  onCancel?: () => void;
}

const AlfredAddBankAccountForm: React.FC<Props> = ({ sourceCurrency, submitting, error, onSubmit, onCancel }) => {
  const { t } = useTranslation();
  // Country/rail are derived from the parent dropdown's selected fiat —
  // Alfred enforces `customer.country == operation.country` (errorCode
  // 111417) so a given fiat has exactly one valid rail. There's no
  // type-picker UI; the title surfaces the resolved rail and we render
  // only the rail-specific inputs.
  const inferredType = useMemo<AlfredFiatAccountType | null>(() => {
    if (!sourceCurrency) return null;
    const country = ALFRED_FIAT_CURRENCY_TO_COUNTRY[sourceCurrency.toUpperCase()];
    return country ? ALFRED_FIAT_ACCOUNT_TYPE_BY_COUNTRY[country] ?? null : null;
  }, [sourceCurrency]);
  const inferredCountry =
    sourceCurrency != null ? ALFRED_FIAT_CURRENCY_TO_COUNTRY[sourceCurrency.toUpperCase()] ?? null : null;

  const [fields, setFields] = useState<AlfredFiatAccountFields>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  // Wipe rail-specific fields when the source currency changes (e.g.
  // user switches BRL → MXN before submitting). Schema differs across
  // rails; mixing values would confuse Alfred's validator. Seed any
  // single-option enum (e.g. PIX accountType is always "CPF") so its
  // value is submitted without the user touching a one-item picker.
  useEffect(() => {
    const seed: AlfredFiatAccountFields = {};
    const defs = inferredType ? RAIL_RULES[inferredType] : [];
    defs.forEach((r) => {
      if (r.options?.length === 1) seed[r.key] = r.options[0];
    });
    setFields(seed);
    setTouched({});
  }, [inferredType]);

  const ruleDefs = inferredType ? RAIL_RULES[inferredType] : [];
  const fieldErrors = inferredType
    ? validateFiatAccountFields(inferredType, fields as Record<string, unknown>)
    : {};
  const canSubmit = !!inferredType && Object.keys(fieldErrors).length === 0 && !submitting;

  const setField = (key: string, value: string) => setFields((prev) => ({ ...prev, [key]: value }));
  const setBool = (key: string, value: boolean) => setFields((prev) => ({ ...prev, [key]: value }));
  const markTouched = (key: string) => setTouched((prev) => ({ ...prev, [key]: true }));

  // NB: this component renders INSIDE the parent withdrawal `<form>`,
  // and nested forms are invalid HTML — the browser strips the inner
  // form and re-routes its submit button to the outer onSubmit, which
  // is `handleInitiateWithdrawal`. That used to fire the actual
  // withdrawal on every "Save bank" click, debit the user's balance,
  // and bounce them to the page-level loader. We use `<div>` +
  // explicit click handler to keep the parent form out of the picture.
  const handleSave = () => {
    if (!inferredType || !canSubmit) {
      // Mark every rail field as touched so all errors become visible
      // when the user clicks Save on an incomplete form.
      const next: Record<string, boolean> = {};
      ruleDefs.forEach((r) => (next[r.key] = true));
      setTouched(next);
      return;
    }
    onSubmit({ type: inferredType, fiatAccountFields: fields });
  };

  // Alfred's fiatAccountType is their internal code (ACH_DOM, BANK_USA,
  // B89, etc). The title should surface the user-facing rail name
  // instead — match what the parent dropdown shows. Per-country
  // override; falls back to the raw code (PIX/SPEI/COELSA are already
  // the canonical names).
  const RAIL_DISPLAY: Record<string, string> = {
    ACH: "PSE",         // Colombia
    ACH_DOM: "ACH",     // Dominican Republic
    ACH_CHL: "ACH",     // Chile
    ACH_BOL: "ACH",     // Bolivia
    BANK_USA: "ACH",    // United States
    BANK_CN: "Bank transfer",
    B89: "B89",         // Peru
  };
  const displayType = inferredType ? RAIL_DISPLAY[inferredType] ?? inferredType : null;

  // i18n helpers. Field keys are ALFRED.FIELD.<rail>.<field>.{LABEL,PH,HINT};
  // English stays via `defaultValue` so locales without these keys are
  // unaffected. Validation errors carry their own key + English default.
  const railName = displayType
    ? t(`ALFRED.RAIL.${inferredType}`, { defaultValue: displayType })
    : "";
  const fieldLabel = (def: RuleDef) =>
    t(`ALFRED.FIELD.${inferredType}.${def.key}.LABEL`, { defaultValue: def.label }) + (def.required ? " *" : "");
  const fieldPlaceholder = (def: RuleDef) =>
    def.placeholder != null
      ? t(`ALFRED.FIELD.${inferredType}.${def.key}.PH`, { defaultValue: def.placeholder })
      : undefined;
  const fieldHint = (def: RuleDef) =>
    def.hint != null ? t(`ALFRED.FIELD.${inferredType}.${def.key}.HINT`, { defaultValue: def.hint }) : undefined;
  const fieldErrorText = (e?: FieldError) =>
    e ? t(e.key, { defaultValue: e.default, ...(e.params ?? {}) }) : undefined;

  const title = displayType
    ? inferredCountry
      ? t("ALFRED.ADD_BANK.TITLE", {
          type: railName,
          country: inferredCountry,
          defaultValue: `Add ${displayType} (${inferredCountry}) account`,
        })
      : t("ALFRED.ADD_BANK.TITLE_NO_COUNTRY", { type: railName, defaultValue: `Add ${displayType} account` })
    : t("ALFRED.ADD_BANK.TITLE_FALLBACK", { defaultValue: "Add bank account" });

  return (
    <div className="alfred-add-bank-form" role="group" aria-label={title}>
      <h3 className="alfred-add-bank-form__title">{title}</h3>

      {ruleDefs.map((def) => {
        const err = touched[def.key] ? fieldErrors[def.key] : undefined;
        if (def.inputType === "checkbox") {
          const checked = fields[def.key] === true;
          return (
            <label key={def.key} className="alfred-add-bank-form__checkbox">
              <input
                type="checkbox"
                checked={checked}
                onChange={(e) => {
                  setBool(def.key, e.target.checked);
                  markTouched(def.key);
                }}
                disabled={submitting}
              />
              <span>{fieldLabel(def)}</span>
            </label>
          );
        }
        // Closed-set field → render a dropdown so the user can't typo
        // an enum value (Alfred rejects unknown with 110002). The
        // dropdown uses the same shared component as the parent form's
        // currency/payment-method pickers for visual consistency.
        if (def.options?.length) {
          const value = ((fields as Record<string, unknown>)[def.key] as string) ?? "";
          // Single allowed value (e.g. PIX → CPF): there's nothing to
          // choose, so show it pre-filled and locked instead of a
          // one-item dropdown the user would have to click anyway.
          if (def.options.length === 1) {
            return (
              <div key={def.key} className="alfred-add-bank-form__field">
                <span className="alfred-add-bank-form__label">{fieldLabel(def)}</span>
                <div className="alfred-add-bank-form__locked">{def.options[0]}</div>
                {fieldHint(def) && <small className="alfred-add-bank-form__hint">{fieldHint(def)}</small>}
              </div>
            );
          }
          return (
            <div key={def.key} className="alfred-add-bank-form__field">
              <DropdownAnimation
                label={fieldLabel(def)}
                selectedValue={value}
                options={def.options.map((opt) => ({ key: opt, value: opt }))}
                placeholder={fieldPlaceholder(def) ?? t("ALFRED.ADD_BANK.SELECT", { defaultValue: "Select…" })}
                onChange={(field) => {
                  setField(def.key, field.key);
                  markTouched(def.key);
                }}
                disabled={submitting}
              />
              {fieldHint(def) && <small className="alfred-add-bank-form__hint">{fieldHint(def)}</small>}
            </div>
          );
        }
        return (
          <div key={def.key} className="alfred-add-bank-form__field">
            <Field
              label={fieldLabel(def)}
              value={((fields as Record<string, unknown>)[def.key] as string) ?? ""}
              onChange={(v) => setField(def.key, v)}
              onBlur={() => markTouched(def.key)}
              placeholder={fieldPlaceholder(def)}
              disabled={submitting}
              error={fieldErrorText(err)}
            />
            {fieldHint(def) && <small className="alfred-add-bank-form__hint">{fieldHint(def)}</small>}
          </div>
        );
      })}

      <ErrorAlert message={error} />

      <div className="alfred-add-bank-form__actions">
        {onCancel && (
          <button
            type="button"
            className="alfred-add-bank-form__cancel"
            onClick={onCancel}
            disabled={submitting}
          >
            {t("ALFRED.ADD_BANK.CANCEL", { defaultValue: "Cancel" })}
          </button>
        )}
        <Button
          title={t("ALFRED.ADD_BANK.SAVE", { defaultValue: "Save bank" })}
          type="button"
          onClick={handleSave}
          disabled={!canSubmit}
          pending={submitting}
          className="deposit-button"
        />
      </div>
    </div>
  );
};

export default AlfredAddBankAccountForm;
